import { useTranslation } from 'language/translator'

import { useMetric } from 'redux/hooks/Metrics'

import { CustomPeriodFilter } from 'types/GlobalWidget'
import { Context } from 'types/GlobalUserEvents'
import { TMetricQueryParams } from 'types/GlobalMetric'

import { getMetricTitle } from 'utils/metric'

import { buildMetricSource } from './builders'
import { TDataDialogEventContext, TDataDialogSource } from './types'
import { TAnalysis } from 'redux/hooks/Analyses'

/** What a page hands DataDialog on top of `onClose` and `depth`. */
export interface TAnalysisDialogProps {
  source: TDataDialogSource | null
  baseFilters: TMetricQueryParams['filter'] | undefined
  basePeriod: CustomPeriodFilter | null
  eventContext: TDataDialogEventContext
  segmentFilters: Record<string, never>
  segmentLabel: null
  segmentPeriod: null
}

/**
 * How an analysis opens its underlying data.
 */
export function useAnalysisDialog(analysis: TAnalysis | null): {
  dialogProps: (realEstates: string[] | null) => TAnalysisDialogProps
  selectedRealEstates: string[]
  disabled: boolean
  tooltip: string
} {
  const { lang, t, translateEnum } = useTranslation()

  // Nullable so a page can call it before the analysis has loaded.
  const analysisMetric = analysis?.metric ?? null
  const metric = useMetric(analysisMetric?.id, null)

  const selectedRealEstates = (analysis?.properties ?? [])
    .filter((property) => property.selected)
    .map((property) => property.name)

  const dialogProps = (realEstates: string[] | null): TAnalysisDialogProps => {
    const settings = analysisMetric?.settings ?? null

    return {
      // DataDialog treats a null source as closed, and stays mounted.
      source:
        realEstates && analysis && analysisMetric && settings
          ? buildMetricSource({
            id: analysisMetric.id,
            settings,
            entityId: metric?.entity?.id ?? null,
            title: metric
              ? getMetricTitle(metric, settings, lang, translateEnum)
              : analysis.title,
            unit: metric?.unit ?? null,
            chatSourceId: analysis.id
          })
          : null,
      baseFilters: analysis?.metric_filter_attribute_id
        ? { [analysis.metric_filter_attribute_id]: { eq: realEstates ?? [] } }
        : undefined,
      basePeriod: analysis
        ? { from: analysis.created_at, to: analysis.created_at }
        : null,
      eventContext: {
        context: Context.FROM_INSIGHT,
        analysisId: analysis?.id ?? null
      },
      segmentFilters: {},
      segmentLabel: null,
      segmentPeriod: null
    }
  }

  const getTooltip = () => {
    if (!analysisMetric) {
      return t('analyses', 'investigate_disabled_tooltip')
    }

    if (selectedRealEstates.length === 0) {
      return t('analyses', 'investigate_no_selection_tooltip')
    }

    return t('analyses', 'investigate_selection')
  }

  return {
    dialogProps,
    selectedRealEstates,
    disabled: !analysisMetric || selectedRealEstates.length === 0,
    tooltip: getTooltip()
  }
}
